"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Mic,
  Scissors,
  Video,
  Sparkles,
  Settings,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";

const categoryIcons: LucideIcon[] = [Mic, Scissors, Video, Sparkles, Settings];
const categoryKeys = ["allf.cat1", "allf.cat2", "allf.cat3", "allf.cat5", "allf.cat6"];

export function AllFeatures() {
  const { T } = useLang();
  const U = useUpper();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            className="inline-block text-sm font-bold text-cyan-600 dark:text-cyan-300 tracking-wider bg-cyan-50 dark:bg-cyan-500/15 px-4 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-400/30 mb-4"
          >
            {U(T("allf.badge"))}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mt-4">
            {T("allf.title1")}
            <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">
              {T("allf.title2")}
            </span>
          </h2>
          <p className="mt-4 text-lg text-neutral-600 dark:text-slate-300 max-w-2xl mx-auto">
            {T("allf.desc")}
          </p>
        </motion.div>

        <div className="space-y-3">
          {categoryKeys.map((catKey, i) => {
            const Icon = categoryIcons[i];
            const isOpen = openIndex === i;
            const items = [T(`${catKey}.i1`), T(`${catKey}.i2`), T(`${catKey}.i3`), T(`${catKey}.i4`)];

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.5, type: "spring", stiffness: 120 }}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center gap-4 p-5 rounded-2xl border border-neutral-200 dark:border-white/10 hover:border-cyan-200 dark:hover:border-cyan-400/40 transition-colors duration-200 bg-white dark:bg-slate-900 text-left"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-500/15 dark:to-teal-500/10 border border-cyan-100 dark:border-cyan-400/30 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-cyan-600 dark:text-cyan-300" />
                  </div>
                  <span className="text-lg font-semibold text-neutral-900 dark:text-white flex-1">
                    {catKey === "allf.cat5" ? "CREANEO AI" : T(catKey)}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-neutral-400 dark:text-slate-500 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-2 pb-1 px-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-14">
                          {items.map((item, j) => (
                            <div
                              key={j}
                              className="flex items-start gap-2 text-sm text-neutral-600 dark:text-slate-200"
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 shrink-0" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
